import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllPayments } from "../../store/thunks/paymentsThunk";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import InvoicePaymentModal from "../../modals/InvoicePaymentModal";
import Table from "../../applicationUi/components/Table";
import QuickBooksLogo from "../../assets/quickbooks-1.svg";

const PaymentsPage = () => {
  const dispatch = useDispatch();

  // Safeguard Redux state with null-safe defaults
  const payments = useSelector((state) => state.payments?.payments || []);
  const isLoading = useSelector((state) => state.payments?.status === "loading");
  const currentPage = useSelector((state) => state.payments?.currentPage || 1);
  const totalPages = useSelector((state) => state.payments?.totalPages || 1);
  const totalPayments = useSelector((state) => state.payments?.totalPayments || 0);
  const error = useSelector((state) => state.payments?.error || null);

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });
  const [dateFilter, setDateFilter] = useState("7"); // Default to 7 days

  // Logging for debugging
  console.log("currentPage", currentPage);
  console.log("totalPages", totalPages);
  console.log("totalPayments", totalPayments);
  console.log("payments", payments);

  useEffect(() => {
    dispatch(fetchAllPayments({ 
      page: currentPage,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      dateFilter: dateFilter
    }));
  }, [dispatch, currentPage, sortConfig, dateFilter]);

  const handleRowClick = (clickedPaymentId) => {
    const payment = payments.find((pay) => pay?._id === clickedPaymentId);
    if (payment) {
      setSelectedPayment(payment);
      setShowPaymentModal(true);
    }
  };

  const handleModalClose = () => {
    setShowPaymentModal(false);
    setSelectedPayment(null);
  };

  const handlePageChange = (newPage) => {
    dispatch(fetchAllPayments({ 
      page: newPage,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      dateFilter: dateFilter
    }));
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    console.log("sortConfig", sortConfig);
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  // Check if error is just "No payments found" - treat as empty state, not error
  const isNoPaymentsError = error && (
    (typeof error === "string" && error.toLowerCase().includes("no payments found")) ||
    (typeof error === "object" && error.message && error.message.toLowerCase().includes("no payments found")) ||
    (typeof error === "object" && error.error && error.error.toLowerCase().includes("no payments found"))
  );

  // Only show error if it's a real error (not just "no payments found")
  if (error && !isNoPaymentsError) {
    const errorMessage =
      typeof error === "object" && error.message
        ? error.message
        : typeof error === "string"
        ? error
        : "An error occurred";
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Payments</h3>
          <p className="text-sm text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Show empty state if no payments (including the "no payments found" error case)
  const showEmptyState = !isLoading && (payments.length === 0 || isNoPaymentsError);

  return (
    <div>
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4 mx-auto"></div>
            <p className="text-gray-600">Loading payments...</p>
          </div>
        </div>
      ) : showEmptyState ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Payments Found</h3>
            <p className="text-sm text-gray-600 mb-4">
              There are no payments for the selected time period. Payments will appear here once they are recorded.
            </p>
            {dateFilter !== "all" && (
              <button
                onClick={() => setDateFilter("all")}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-sm shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
              >
                View All Time
              </button>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="py-4 flex justify-between items-center">
            <div className="flex-1 mr-4">
              <SearchDropdown
                prefix=":payment:"
                displayFields={[
                  "invoiceId.invoiceId",
                  "paymentMethod",
                  "amount",
                  "invoiceId.client.fullName",
                ]}
                onSelection={(selectedPayment) => {
                  setSelectedPayment(selectedPayment);
                  setShowPaymentModal(true);
                }}
                placeholder={
                  "Search for a payment by invoice ID, client name, or payment method"
                }
              />
            </div>
            <div className="w-48">
              <select
                value={dateFilter}
                onChange={handleDateFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-sm shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>

          <Table
            columns={[
              {
                header: "Invoice IDs",
                accessor: (row) => {
                  if (!row?.invoiceId?.length) return "N/A";
                  return row.invoiceId.map(inv => inv.invoiceId).join(", ");
                },
                sortable: true,
              },
              {
                header: "Client Names",
                accessor: (row) => {
                  if (!row?.invoiceId?.length) return "N/A";
                  const clientNames = row.invoiceId.map(inv => {
                    const client = inv.client;
                    return client ? `${client.firstName} ${client.lastName}` : "N/A";
                  });
                  return [...new Set(clientNames)].join(", "); // Remove duplicates
                },
              },
              {
                header: "Amount",
                accessor: (row) => `$${row?.amount?.toFixed(2) || "0.00"}`,
                sortable: true,
              },
              {
                header: "Payment Date",
                accessor: (row) => {
                  if (!row?.paymentDate) return "N/A";
                  const date = new Date(row.paymentDate);
                  return date.toLocaleDateString("en-US", {
                    year: "numeric",
                    weekday: 'short',
                    month: "short",
                    day: "numeric",
                  });
                },
                sortable: true,
              },
              {
                header: "Payment Method",
                accessor: (row) => row?.paymentMethod || "N/A",
                sortable: true,
              },
              {
                header: "QuickBooks",
                accessor: (row) => (
                  row?.QBOPaymentId ? (
                    <img
                      src={QuickBooksLogo}
                      alt="QuickBooks"
                      className="ml-2 h-5 w-5"
                      title="Loaded to QuickBooks"
                    />
                  ) : null
                ),
              },
            ]}
            data={payments}
            onRowClick={handleRowClick}
            title="Payments"
            description={`List of all payments (${totalPayments} total)`}
            showPagination={true}
            currentPage={currentPage}
            totalResults={totalPayments}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            sortConfig={sortConfig}
            onSortChange={handleSort}
          />

          {showPaymentModal && selectedPayment && (
            <InvoicePaymentModal
              payment={selectedPayment || {}}
              onClose={handleModalClose}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
